import React, { useEffect, useState, useRef, useCallback } from 'react';
import { checkLogin, host } from '../script/variables';
import Nav from '../common/Nav';
import Editor from './Editor';
import uploadViaPresign from '../script/uploadViaPresignV2';
import './style/writeblog.css';

/* ── debounce helper ── */
const useDebounce = (fn, delay) => {
  const timer = useRef(null);
  return useCallback((...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
};

const WriteBlog = () => {
  const [isLogin,        setIsLogin]        = useState(false);
  const [title,          setTitle]          = useState('');
  const [titleAv,        setTitleAv]        = useState(null); // null = unchecked
  const [checking,       setChecking]       = useState(false);

  /* ── thumbnail state ── */
  const [thumbnail,      setThumbnail]      = useState('');      // final S3 URL
  const [thumbFile,      setThumbFile]      = useState(null);    // File object
  const [thumbPreview,   setThumbPreview]   = useState('');      // local blob URL
  const [thumbUploading, setThumbUploading] = useState(false);
  const [thumbError,     setThumbError]     = useState('');
  const thumbInputRef = useRef(null);

  const token = localStorage.getItem('token');

  /* ── auth ── */
  useEffect(() => {
    (async () => {
      const login = await checkLogin();
      setIsLogin(login);
    })();
  }, []);

  /* ── title availability ── */
  const _checkTitle = async (val) => {
    if (!val.trim()) { setTitleAv(null); return; }
    setChecking(true);
    try {
      const res = await fetch(`${host}/blog/checktitle?tname=${encodeURIComponent(val)}`, {
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (res.ok) {
        const data = await res.json();
        setTitleAv(data.length === 0);
      }
    } catch { /* silent */ }
    finally { setChecking(false); }
  };

  const checkTitleAvailability = useDebounce(_checkTitle, 600);

  const handleTitleChange = (e) => {
    const val = e.target.value;
    setTitle(val);
    setTitleAv(null);
    checkTitleAvailability(val);

    /* auto-grow textarea */
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  /* ── thumbnail: file select ── */
  const handleThumbFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setThumbError('');

    if (file.type !== 'image/jpeg' && file.type !== 'image/png') {
      setThumbError('Only JPEG or PNG files are allowed.');
      return;
    }

    setThumbFile(file);
    setThumbPreview(URL.createObjectURL(file));
    setThumbnail(''); // clear previous confirmed URL until re-uploaded
  };

  const uploadThumbnailToS3 = (file) => uploadViaPresign(file, 'moment');

  const handleThumbUpload = async () => {
    if (!thumbFile) return;
    setThumbUploading(true);
    setThumbError('');
    try {
      const url = await uploadThumbnailToS3(thumbFile);
      setThumbnail(url);
      setThumbFile(null);
    } catch {
      setThumbError('Upload failed. Please try again.');
    } finally {
      setThumbUploading(false);
    }
  };

  const handleThumbRemove = () => {
    setThumbnail('');
    setThumbFile(null);
    setThumbPreview('');
    setThumbError('');
    if (thumbInputRef.current) thumbInputRef.current.value = '';
  };

  /* ── title status indicator ── */
  const TitleStatus = () => {
    if (!title.trim()) return null;
    if (checking)          return <span className="wb-title-status wb-checking">Checking…</span>;
    if (titleAv === true)  return <span className="wb-title-status wb-avail">✓ Available</span>;
    if (titleAv === false) return <span className="wb-title-status wb-taken">✗ Title already taken</span>;
    return null;
  };

  /* ── thumbnail status badge ── */
  const ThumbBadge = () => {
    if (thumbUploading)  return <span className="wb-title-status wb-checking">Uploading…</span>;
    if (thumbnail)       return <span className="wb-title-status wb-avail">✓ Uploaded</span>;
    if (thumbFile)       return <span className="wb-title-status wb-checking">Ready to upload</span>;
    return null;
  };

  return (
    <>
      <Nav isLogin={isLogin} setIsLogin={setIsLogin} vegMode={false} />

      <div className="wb-shell">
        <div className="wb-inner">

          {/* ── Page heading ── */}
          <div className="wb-page-header">
            <h1 className="wb-page-title">Write a post</h1>
            <p className="wb-page-sub">Share your story with the Restro Pilots community</p>
          </div>

          {/* ── Title field ── */}
          <div className="wb-title-wrap">
            <textarea
              className="wb-title-input"
              value={title}
              onChange={handleTitleChange}
              placeholder="Post title…"
              rows={1}
              spellCheck
            />
            <TitleStatus />
          </div>

          {/* ── Thumbnail uploader ── */}
          <div className="wb-thumb-section">
            <div className="wb-thumb-header">
              <span className="wb-thumb-label">Thumbnail</span>
              <ThumbBadge />
            </div>

            {/* Preview area */}
            {(thumbPreview || thumbnail) ? (
              <div className="wb-thumb-preview-wrap">
                <img
                  className="wb-thumb-preview"
                  src={thumbnail || thumbPreview}
                  alt="Thumbnail preview"
                />
                {/* Overlay badge: shows "Pending upload" if local-only */}
                {thumbFile && !thumbnail && (
                  <div className="wb-thumb-overlay">Not uploaded yet</div>
                )}
                <button
                  className="wb-thumb-remove"
                  onClick={handleThumbRemove}
                  title="Remove thumbnail"
                >
                  ✕
                </button>
              </div>
            ) : (
              <label className="wb-thumb-dropzone" htmlFor="wb-thumb-input">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="3"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span>Click to choose a thumbnail</span>
                <span className="wb-thumb-hint">JPEG or PNG · Recommended 1280 × 720</span>
              </label>
            )}

            {/* File input (hidden, triggered by dropzone label or change button) */}
            <input
              id="wb-thumb-input"
              ref={thumbInputRef}
              type="file"
              accept="image/jpeg,image/png"
              style={{ display: 'none' }}
              onChange={handleThumbFileChange}
            />

            {/* Action row */}
            <div className="wb-thumb-actions">
              {(thumbPreview || thumbnail) && (
                <label className="wb-thumb-change-btn" htmlFor="wb-thumb-input">
                  Change image
                </label>
              )}

              {/* Show Upload button only when a new file is selected but not yet pushed to S3 */}
              {thumbFile && !thumbnail && (
                <button
                  className="wb-thumb-upload-btn"
                  onClick={handleThumbUpload}
                  disabled={thumbUploading}
                >
                  {thumbUploading ? 'Uploading…' : 'Upload thumbnail'}
                </button>
              )}
            </div>

            {thumbError && <p className="wb-thumb-error">{thumbError}</p>}
          </div>

          {/* ── Editor (thumbnail URL passed down so Editor can include it in payload) ── */}
          <div className="wb-editor-wrap">
            <Editor title={title} titleAv={titleAv === true} thumbnail={thumbnail} />
          </div>

        </div>
      </div>
    </>
  );
};

export default WriteBlog;